import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';


// Styled components following SongQ design system
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#121212',
    color: '#fff',
    maxWidth: '1200px',
    width: '95vw',
    maxHeight: '95vh',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  color: '#fff',
  borderBottom: '1px solid #333',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  backgroundColor: '#121212',
  padding: '16px',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#1A1A1A',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#333',
    borderRadius: '4px',
  },
}));

const QuestionCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
  '&:last-child': {
    marginBottom: 0,
  },
}));

const AnswerOption = styled(Box)(({ theme, isCorrect }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 14px',
  margin: '6px 0',
  borderRadius: '8px',
  backgroundColor: isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
  border: isCorrect ? '1px solid #4CAF50' : '1px solid #f44336',
  '& .MuiSvgIcon-root': {
    marginRight: '12px',
    color: isCorrect ? '#4CAF50' : '#f44336',
  },
}));

const MetadataChip = styled(Chip)(({ theme }) => ({
  margin: '4px 8px 4px 0',
  fontWeight: 500,
  fontSize: '0.75rem',
  '&.difficulty': {
    backgroundColor: '#4CAF50',
    color: '#fff',
  },
  '&.answer-type': {
    backgroundColor: '#2196F3',
    color: '#fff',
  },
  '&.mood': {
    backgroundColor: '#FF5722',
    color: '#fff',
  },
  '&.topic': {
    backgroundColor: '#607D8B',
    color: '#fff',
  },
  '&.time': {
    backgroundColor: '#795548',
    color: '#fff',
  },
}));

const QuestionPreviewModal = ({ open, onClose, song, questionIds = [] }) => {
  const [questionsWithAnswers, setQuestionsWithAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch questions with answers when modal opens
  useEffect(() => {
    const fetchQuestionsWithAnswers = async () => {
      setLoading(true);
      setError(null);

      try {
        const API_BASE = process.env.REACT_APP_API_URL;
        const questionsData = [];

        for (const questionId of questionIds) {
          const response = await fetch(`${API_BASE}/api/admin/questions/${questionId}`);
          if (response.ok) {
            const questionData = await response.json();
            // The getQuestionWithDetails already includes answers
            questionsData.push(questionData);
          }
        }

        setQuestionsWithAnswers(questionsData);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError('Failed to load questions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (open && questionIds.length > 0) {
      fetchQuestionsWithAnswers();
    }
  }, [open, questionIds]);

  const getDifficultyColor = (level) => {
    const colors = {
      'Easy': '#4CAF50',
      'Medium': '#FF9800',
      'Hard': '#F44336',
      'Very Hard': '#9C27B0',
      'Expert': '#E91E63'
    };
    return colors[level] || '#2196F3';
  };

  const getAnswerTypeColor = (type) => {
    const colors = {
      'single_choice': '#2196F3',
      'multiple_choice': '#9C27B0',
      'true_false': '#FF9800'
    };
    return colors[type] || '#2196F3';
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Question Preview: {song?.song_name}
          </Typography>
          {song?.artist && (
            <Typography variant="body2" sx={{ color: '#ccc', mt: 0.5 }}>
              by {song.artist} • {questionIds.length} questions
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#ccc' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress sx={{ color: '#4CAF50' }} />
            <Typography sx={{ ml: 2, color: '#ccc' }}>Loading questions...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#fff' }}>
            {error}
          </Alert>
        ) : questionsWithAnswers.length === 0 ? (
          <Alert severity="info" sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)', color: '#fff' }}>
            No questions found for this song.
          </Alert>
        ) : (
          questionsWithAnswers.map((question, index) => (
            <QuestionCard key={question.question_id}>
              {/* Question Header */}
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600, mr: 2 }}>
                  Question {index + 1}
                </Typography>
                <Box display="flex" flexWrap="wrap" alignItems="center">
                  <MetadataChip
                    label={question.level_name || 'Medium'}
                    className="difficulty"
                    size="small"
                    sx={{ backgroundColor: getDifficultyColor(question.level_name) }}
                  />
                  <MetadataChip
                    label={question.answer_type_display || question.answer_type || 'Multiple Choice'}
                    className="answer-type"
                    size="small"
                    sx={{ backgroundColor: getAnswerTypeColor(question.answer_type) }}
                  />
                  {question.mood_name && (
                    <MetadataChip
                      label={question.mood_name}
                      className="mood"
                      size="small"
                    />
                  )}
                  {question.topic_name && (
                    <MetadataChip
                      label={question.topic_name}
                      className="topic"
                      size="small"
                    />
                  )}
                  {/* <MetadataChip
                    label={formatTimeLimit(question.time_limit_seconds || 30)}
                    className="time"
                    size="small"
                  /> */}
                </Box>
              </Box>

              {/* Question Text */}
              <Typography variant="body1" sx={{ color: '#fff', mb: 3, lineHeight: 1.6 }}>
                {question.question_text}
              </Typography>

              {/* Question Clue */}
              {question.question_clue && (
                <Box mb={3}>
                  <Typography variant="body2" sx={{ color: '#FFB74D', fontStyle: 'italic' }}>
                    💡 Hint: {question.question_clue}
                  </Typography>
                </Box>
              )}

              {/* Answer Options */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 2 }}>
                  Answer Options:
                </Typography>
                {question.answers && question.answers.length > 0 ? (
                  question.answers.map((answer, answerIndex) => (
                    <AnswerOption key={answer.answer_id || answerIndex} isCorrect={answer.is_correct}>
                      {answer.is_correct ? (
                        <CheckCircleIcon />
                      ) : (
                        <CancelIcon />
                      )}
                      <Box flex={1}>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {answer.answer_text}
                        </Typography>
                        {answer.answer_reason && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: answer.is_correct ? '#A5D6A7' : '#FFCDD2',
                              mt: 0.5,
                              display: 'block'
                            }}
                          >
                            {answer.is_correct ? '✓' : '✗'} {answer.answer_reason}
                          </Typography>
                        )}
                      </Box>
                    </AnswerOption>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                    No answer options available
                  </Typography>
                )}
              </Box>

              {index < questionsWithAnswers.length - 1 && (
                <Divider sx={{ mt: 3, borderColor: '#333' }} />
              )}
            </QuestionCard>
          ))
        )}
      </StyledDialogContent>

      <DialogActions sx={{ backgroundColor: '#1A1A1A', borderTop: '1px solid #333', p: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: '#333',
            color: '#ccc',
            '&:hover': {
              borderColor: '#4CAF50',
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default QuestionPreviewModal;
